import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import { signIn } from 'next-auth/react';
import axios, { Method } from 'axios';
import { LookerSignedResponse } from 'lib/looker/types';
import fnNoOp from 'lib/util/no-op';
import { logError, logInfo, logWarning, makeLoggableError } from 'lib/util/errors';

/**
 * useRefreshEmbedOnTimeout hook supports reloading looker embed urls on a timeout
 */
export const useRefreshEmbedOnTimeout = ({
  embedIdValue,
  isEE,
  disableLoadingScreen,
}: {
  embedIdValue?: number;
  disableLoadingScreen: boolean;
  isEE: boolean;
}) => {
  const [embedUrl, setEmbedUrl] = useState('');
  const [isIFrameLoaded, setIFrameLoaded] = useState(disableLoadingScreen);
  const [hadUnexpectedError, setHadUnexpectedError] = useState(false);

  /**
   * Hook to load new looker embed urls before they expire
   */
  useEffect(() => {
    // Only set the interval when running client-side and not in experience editor
    if (isEE || typeof window === 'undefined') {
      return fnNoOp;
    }
    // Setup an abortcontroller to kill pending requests on unmount
    const cancelRequestController = new AbortController();
    const cancelRequestSignal = cancelRequestController.signal;
    let intervalId = -1;
    // minutes * seconds * milliseconds
    const embedRefreshTimeout = 9 * 60 * 1000;
    /**
     * Retrieves an up-to-date embed url from the server
     */
    const getNewEmbedUrl = async () => {
      // Request new embed URL from back-end API
      const requestMethod: Method = 'POST';
      return axios
        .request({
          method: requestMethod,
          url: '/api/looker/embed',
          data: `embedId=${embedIdValue ?? ''}`,
          headers: {
            'content-type': 'application/x-www-form-urlencoded',
            cache: 'no-cache',
          },
          signal: cancelRequestSignal,
        })
        .then((wsoResponse) => {
          // Axios will auto-follow redirects in client-side code, but the redirected response data will be a string
          if (typeof wsoResponse.data !== 'string') {
            // We got JSON back, and didn't fall into our catch, so return the url parameter.
            return (wsoResponse.data as LookerSignedResponse)?.u ?? '';
          }
          // Otherwise, if the response's request url contains the okta login slug
          if (/\/auth\/okta/i.test(wsoResponse.request.responseURL)) {
            // Let NextAuth do it's thing to re-establish a session
            signIn();
          }
          logWarning('Unexpected looker service response detected.', { data: wsoResponse.data });
          // If we made it here we didn't get an updated embed, return empty string
          return '';
        });
    };
    /**
     * RefreshEmbed timer callback
     */
    const refreshEmbed = async () => {
      // Get a new embed identifier
      return getNewEmbedUrl()
        .then((x) => {
          // Hide the iframe as it refreshes
          if (!disableLoadingScreen) {
            setIFrameLoaded(false);
          }
          setEmbedUrl(x);
          // If we got back an empty embed url then something went wrong...
          setHadUnexpectedError(x === '');
          // And return the embed url in case the caller cares
          return x;
        })
        .catch((error) => {
          // Check to see if we cancelled the request (eg component was unmounted)
          if (!axios.isCancel(error)) {
            // Otherwise log error and update state
            logError(
              `LookerEmbed:EmbedRequestFailure`,
              makeLoggableError({ error, name: 'Looker Service Failure' })
            );
            setHadUnexpectedError(true);
          }
        });
    };
    // Call API to get embed url
    refreshEmbed().then(() => {
      // And setup our interval callback to refresh before the embed times out
      intervalId = window.setInterval(() => refreshEmbed(), embedRefreshTimeout);
    });
    return () => {
      // Kill any pending requests so we don't update state on an unmounted component
      cancelRequestController.abort();
      // Validated window exists at top of method, safe to call on unmount
      window.clearInterval(intervalId);
    };
  }, [
    embedIdValue,
    disableLoadingScreen,
    isEE,
    setIFrameLoaded,
    setEmbedUrl,
    setHadUnexpectedError,
  ]);

  return {
    embedUrl,
    isIFrameLoaded,
    setIFrameLoaded,
    hadUnexpectedError,
    setHadUnexpectedError,
  };
};

/**
 * data passed in message by looker
 */
type LookerFrameMessage = {
  type: string;
  height?: number;
  status?: string;
  errors?: unknown[];
};

const getLookerFrameHeight = (messageData: LookerFrameMessage, defaultValue?: string): string => {
  const frameHeightFromMessage = messageData.height ?? 0;
  return frameHeightFromMessage ? `${frameHeightFromMessage + 10}px` : defaultValue ?? '100px';
};

/**
 * Hook to listen for looker events
 */
export const useLookerMessageListener = ({
  isEE,
  disableLoadingScreen,
  theIframeId,
  setIFrameLoaded,
}: {
  theIframeId: string;
  isEE: boolean;
  disableLoadingScreen: boolean;
  setIFrameLoaded: Dispatch<SetStateAction<boolean>>;
}) => {
  useEffect(() => {
    // No hook neeed in experience editor or when loading screen disabled
    if (isEE || typeof window === 'undefined') {
      return fnNoOp;
    }
    const onWindowMessage = (message: MessageEvent) => {
      const iframeElement = window.document.getElementById(theIframeId) as HTMLIFrameElement;
      if (iframeElement && message.source === iframeElement.contentWindow) {
        try {
          const messageData = JSON.parse(message.data) as LookerFrameMessage;
          switch (messageData.type) {
            case 'dashboard:run:complete':
              setIFrameLoaded(true);
              logInfo('embed finished loading.', messageData);
              break;
            case 'page:properties:changed':
              iframeElement.height = getLookerFrameHeight(messageData, iframeElement.height);
              logInfo('embed frame resized', { data: messageData });
              break;
            case 'dashboard:tile:complete':
              if (messageData.status !== 'complete') {
                logError(
                  'LookerEmbed',
                  makeLoggableError({
                    name: 'LookerEmbedTileError',
                    error: {
                      message: JSON.stringify(messageData.errors ?? []),
                    },
                  })
                );
                axios
                  .request({
                    method: 'post',
                    url: '/api/looker/error',
                    data: message.data,
                    headers: {
                      'content-type': 'application/json',
                      cache: 'no-cache',
                    },
                  })
                  .catch((e) =>
                    logError(
                      'LookerEmbed: Failed to post error details',
                      makeLoggableError({ error: e })
                    )
                  );
              }
              break;
            default:
              logInfo('LookerEmbed has recieved a message.', { messageData });
              break;
          }
        } catch (e) {
          logError(
            'LookerEmbed',
            makeLoggableError({ error: e, name: 'Message data parse failure' })
          );
        }
      }
    };
    window.addEventListener('message', onWindowMessage);
    return () => {
      window.removeEventListener('message', onWindowMessage);
    };
  }, [theIframeId, setIFrameLoaded, isEE, disableLoadingScreen]);
};
